import { useMemo, useRef, useState } from "react";
import {
  X, Check, ChevronLeft, ChevronRight, Search, Target, FileText,
  Landmark, Ban, AlertTriangle, Eye, UploadCloud, Loader2,
} from "lucide-react";
import { T } from "../../ui/tokens";
import { fmt, fmtDate, pct } from "../../lib/format";
import { useFaturasPendentes } from "./useFaturasPendentes";
import { scoreProximidade, dentroDoIntervaloPlausivel, dataImpossivel, precisaDecisao, openPdf } from "./matching";
import DropFaturaNova from "./DropFaturaNova";
import DrivePreview from "./DrivePreview";
import ExtratoPreview from "./ExtratoPreview";
import { ACCEPT_EXTRATO, mensagemErroImportacao } from "../../lib/extratoFormatos";
import { apiFetch } from "../../lib/api";
import type { FaturaPendente, MovimentoExtrato } from "./types";

interface Props {
  movimentos: MovimentoExtrato[];
  idInicial: number;
  empresa: string;
  periodo?: string;
  busy: boolean;
  erro?: string | null;
  onConfirmarSugestao: (matchId: number) => void;
  onRejeitarSugestao: (matchId: number) => void;
  onAssociar: (movimento: MovimentoExtrato, faturaIds: number[]) => void;
  onResolverSemFatura: (movimento: MovimentoExtrato) => void;
  onExtratoSubstituido: () => void;
  onClose: () => void;
}

// Painel guiado de conciliação: em vez de uma tabela densa onde é preciso
// caçar linha a linha (e dois modais diferentes conforme o estado), abre uma
// fila com TODOS os movimentos que ainda precisam de decisão nesse mês e
// percorre-os um a um, sempre com o documento do banco à esquerda e a fatura
// à direita. Substitui ModalConfirmarMatch + ModalCorrigirFatura no fluxo
// normal (os dois continuam a existir para entradas pontuais pela tabela).
//
// A FILA é fixada quando o painel abre (snapshot de ids) e a navegação é
// sobre ela — mas cada movimento é relido da lista viva a cada render, para
// refletir o resultado da ação anterior. Guardar índices sobre um array que
// muda a cada refetch fazia o painel saltar para o movimento errado depois
// de cada confirmação.
export default function PainelConciliacao({
  movimentos, idInicial, empresa, periodo, busy, erro,
  onConfirmarSugestao, onRejeitarSugestao, onAssociar, onResolverSemFatura, onExtratoSubstituido, onClose,
}: Props) {
  const [filaIds] = useState<number[]>(() => {
    const fila = movimentos.filter(precisaDecisao).map((m: MovimentoExtrato) => m.id);
    // Entrar por uma linha que já está resolvida (clique direto na tabela)
    // continua a ter de mostrar essa linha — junta-se à frente da fila.
    return fila.includes(idInicial) ? fila : [idInicial, ...fila];
  });
  const [pos, setPos] = useState(() => Math.max(0, filaIds.indexOf(idInicial)));
  const [filtro, setFiltro] = useState("");
  const [selecionadas, setSelecionadas] = useState<Set<number>>(new Set());
  const [aTrocarFatura, setATrocarFatura] = useState(false);
  const [aTrocarExtrato, setATrocarExtrato] = useState(false);
  const [erroTrocaExtrato, setErroTrocaExtrato] = useState<string | null>(null);
  const inputExtratoRef = useRef<HTMLInputElement>(null);

  const idAtual = filaIds[pos];
  const m = movimentos.find((x: MovimentoExtrato) => x.id === idAtual);

  const tipo = m && m.tipo === "credito" ? "cliente" : "fornecedor";
  const { data: dadosFaturas, isLoading: carregandoFaturas } = useFaturasPendentes(empresa, tipo);
  const listaCompleta = useMemo(() => dadosFaturas?.faturas || [], [dadosFaturas]);

  const resolvidos = filaIds.filter((id) => {
    const mov = movimentos.find((x: MovimentoExtrato) => x.id === id);
    return mov && !precisaDecisao(mov);
  }).length;

  // Sem pesquisa mostra só candidatos plausíveis (data/valor próximos);
  // ao pesquisar, procura em TODAS as faturas pendentes com data possível —
  // pode ser precisamente um caso fora do intervalo habitual de mês/valor,
  // mas nunca uma fatura datada depois do movimento (fisicamente
  // impossível, ver dataImpossivel em matching.ts — regra absoluta, sem
  // exceção nem por pesquisa, pedido direto do dono do projeto).
  const candidatos = useMemo(() => {
    if (!m) return [];
    const f = filtro.trim().toLowerCase();
    const valorFiltro = parseFloat(filtro.trim().replace(",", "."));
    const temValorFiltro = filtro.trim() !== "" && !Number.isNaN(valorFiltro);
    const semImpossiveis = listaCompleta.filter((c: FaturaPendente) => !dataImpossivel(c, m));
    const base = !f
      ? semImpossiveis.filter((c: FaturaPendente) => dentroDoIntervaloPlausivel(c, m))
      : semImpossiveis.filter((c: FaturaPendente) =>
          (c.nome_emitente || "").toLowerCase().includes(f) ||
          (c.numero_documento || "").toLowerCase().includes(f) ||
          (temValorFiltro && Math.abs(Math.abs(c.valor_total || 0) - Math.abs(valorFiltro)) < 0.01)
        );
    const idsBase = new Set(base.map((c: FaturaPendente) => c.id));
    const fora = semImpossiveis.filter((c: FaturaPendente) => selecionadas.has(c.id) && !idsBase.has(c.id));
    return [...fora, ...base].sort((a: FaturaPendente, b: FaturaPendente) => scoreProximidade(b, m) - scoreProximidade(a, m));
  }, [listaCompleta, filtro, m, selecionadas]);

  const irPara = (novaPos: number) => {
    setPos(Math.max(0, Math.min(filaIds.length - 1, novaPos)));
    setFiltro("");
    setSelecionadas(new Set());
    setATrocarFatura(false);
  };
  const seguinte = () => irPara(pos + 1);

  const trocarExtrato = async (file: File) => {
    if (!m?.importacao_id) return;
    setATrocarExtrato(true);
    setErroTrocaExtrato(null);
    const enviar = async (confirmar: boolean) => {
      const fd = new FormData();
      fd.append("arquivo", file);
      fd.append("empresa_nif", empresa);
      if (m.banco) fd.append("banco", m.banco);
      if (m.conta) fd.append("conta", m.conta);
      fd.append("substituir_importacao_id", m.importacao_id || "");
      if (confirmar) fd.append("confirmar_substituicao", "true");
      return apiFetch("/api/extratos/importar-arquivo", { method: "POST", body: fd });
    };
    try {
      const preview = await enviar(false);
      const dadosPreview = await preview.json().catch(() => ({}));
      if (!preview.ok) throw new Error(mensagemErroImportacao(dadosPreview?.detail));
      const impacto = dadosPreview?.impacto;
      const confirmou = window.confirm(
        `Substituir o extrato atual?\n\n` +
        `${impacto?.linhas_atuais ?? 0} linha(s) atuais serão removidas.\n` +
        `${impacto?.linhas_novas ?? 0} linha(s) serão importadas do novo ficheiro.\n` +
        `${impacto?.matches_pendentes_a_recalcular ?? 0} sugestão(ões) serão recalculadas.\n\n` +
        `Esta operação só avança se ainda não existirem decisões confirmadas.`
      );
      if (!confirmou) return;
      const resposta = await enviar(true);
      const dados = await resposta.json().catch(() => ({}));
      if (!resposta.ok) throw new Error(mensagemErroImportacao(dados?.detail));
      onExtratoSubstituido();
    } catch (e) {
      setErroTrocaExtrato(e instanceof Error ? e.message : "Não foi possível substituir o extrato.");
    } finally {
      setATrocarExtrato(false);
      if (inputExtratoRef.current) inputExtratoRef.current.value = "";
    }
  };

  if (!m) {
    // O movimento desapareceu da lista viva (filtro mudou, ou foi apagado).
    return (
      <Overlay onClose={onClose}>
        <div style={{ padding: "48px 32px", textAlign: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Este movimento já não está na lista.</div>
          <button onClick={onClose} style={botaoSecundario}>Fechar</button>
        </div>
      </Overlay>
    );
  }

  const valorMov = Math.abs(m.valor);
  const temSugestao = m.match_estado === "pendente" && m.match_id != null;
  const jaResolvido = !precisaDecisao(m);
  const mostrarPicker = !temSugestao || aTrocarFatura;

  const somaSelecionadas = listaCompleta
    .filter((c: FaturaPendente) => selecionadas.has(c.id))
    .reduce((acc: number, c: FaturaPendente) => acc + (c.valor_em_divida ?? c.valor_total ?? 0), 0);
  const pagamentoParcial = selecionadas.size === 1 && valorMov < somaSelecionadas - 0.02;
  const excedeDivida = selecionadas.size > 0 && valorMov > somaSelecionadas + 0.02;
  const divergeSoma = selecionadas.size > 1 && Math.abs(somaSelecionadas - valorMov) > 0.02;
  // Uma única fatura pode receber um pagamento parcial; nunca se aceita um
  // movimento acima do saldo em dívida. Lotes de várias faturas continuam a
  // exigir soma exata para não haver rateios implícitos.
  const bloqueadoPorSoma = excedeDivida || divergeSoma;

  const valorFatura = m.fatura_valor ?? 0;
  const divergeValorSugestao = temSugestao && Math.abs(Math.abs(valorFatura) - valorMov) > 0.02;

  return (
    <Overlay onClose={busy ? undefined : onClose}>
      {/* Cabeçalho: progresso + navegação */}
      <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexShrink: 0 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14.5, fontWeight: 800, color: T.text }}>
            Conciliar {periodo || "todos os meses"}
          </div>
          <div style={{ fontSize: 12, color: T.textMid, marginTop: 2 }}>
            {resolvidos} de {filaIds.length} resolvidos · a ver o {pos + 1}º
          </div>
        </div>

        <div style={{ flex: 1, maxWidth: 260, height: 6, background: T.border, borderRadius: 99, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${filaIds.length ? (resolvidos / filaIds.length) * 100 : 0}%`, background: T.green, transition: "width .3s ease" }} />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <button disabled={busy || pos === 0} onClick={() => irPara(pos - 1)} title="Anterior" style={botaoIcone(busy || pos === 0)}>
            <ChevronLeft size={16} strokeWidth={2.5} />
          </button>
          <button disabled={busy || pos >= filaIds.length - 1} onClick={seguinte} title="Seguinte" style={botaoIcone(busy || pos >= filaIds.length - 1)}>
            <ChevronRight size={16} strokeWidth={2.5} />
          </button>
          <button disabled={busy} onClick={onClose} title="Fechar" style={{ ...botaoIcone(busy), marginLeft: 6 }}>
            <X size={15} strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {jaResolvido && (
        <div style={{ padding: "8px 20px", background: T.greenBg, color: T.green, fontSize: 12, fontWeight: 600, borderBottom: `1px solid ${T.greenBorder}`, display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <Check size={13} strokeWidth={3} />
          {m.resolvido_sem_fatura ? "Marcado como resolvido sem fatura." : "Já conciliado."}
          {pos < filaIds.length - 1 && (
            <button onClick={seguinte} style={{ marginLeft: "auto", border: "none", background: "none", color: T.green, fontWeight: 700, fontSize: 12, cursor: "pointer", textDecoration: "underline" }}>
              Seguinte →
            </button>
          )}
        </div>
      )}

      {/* Corpo: banco à esquerda, fatura à direita */}
      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, background: T.border, minHeight: 0, overflow: "hidden" }}>
        {/* ── Lado do banco ───────────────────────────────────────────── */}
        <div style={{ background: T.surface, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
          <div style={{ padding: "12px 18px", borderBottom: `1px solid ${T.borderSoft}`, flexShrink: 0 }}>
            <div style={{ ...rotulo, color: T.green, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
              <span><Landmark size={11} strokeWidth={2.5} style={{ verticalAlign: -1, marginRight: 4 }} />Linha do banco</span>
              {!!m.importacao_id && <>
                <input ref={inputExtratoRef} type="file" accept={ACCEPT_EXTRATO} hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) trocarExtrato(f); }} />
                <button onClick={() => inputExtratoRef.current?.click()} disabled={busy || aTrocarExtrato} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 8px", border: `1px solid ${T.border}`, borderRadius: 7, background: T.surface, color: T.textMid, fontSize: 10.5, fontWeight: 700, cursor: busy || aTrocarExtrato ? "not-allowed" : "pointer", textTransform: "none", letterSpacing: 0 }}>
                  {aTrocarExtrato ? <Loader2 size={12} className="spin" /> : <UploadCloud size={12} />}
                  {aTrocarExtrato ? "A validar…" : "Trocar extrato"}
                </button>
              </>}
            </div>
            {erroTrocaExtrato && <div style={{ marginTop: 7, padding: "7px 9px", borderRadius: 7, background: T.redBg, color: T.red, fontSize: 11.5, lineHeight: 1.4 }}>{erroTrocaExtrato}</div>}
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginTop: 6, lineHeight: 1.35 }}>{m.descricao}</div>
            <div style={{ fontSize: 12, color: T.textMid, marginTop: 5 }}>
              {fmtDate(m.data_mov)}{m.banco ? ` · ${m.banco}` : ""}{m.conta ? ` · ${m.conta}` : ""}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: m.valor < 0 ? T.red : T.green, marginTop: 6, fontVariantNumeric: "tabular-nums" }}>
              {fmt(m.valor)}
            </div>
          </div>
          <div style={{ flex: 1, minHeight: 0, background: T.bg }}>
            <ExtratoPreview movimentos={movimentos} movimentoAtual={m} empresa={empresa} />
          </div>
        </div>

        {/* ── Lado da fatura ──────────────────────────────────────────── */}
        <div style={{ background: T.surface, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
          <div style={{ padding: "12px 18px", borderBottom: `1px solid ${T.borderSoft}`, flexShrink: 0 }}>
            <div style={{ ...rotulo, color: T.blue, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span>
                <FileText size={11} strokeWidth={2.5} style={{ verticalAlign: -1, marginRight: 4 }} />
                {temSugestao && !aTrocarFatura ? `Fatura sugerida · ${pct(m.match_confianca ?? 0)}` : "Escolher fatura"}
              </span>
              {temSugestao && !aTrocarFatura && (
                <button onClick={() => setATrocarFatura(true)} disabled={busy}
                  style={{ border: "none", background: "none", color: T.textMid, fontSize: 11, fontWeight: 600, cursor: busy ? "default" : "pointer", textDecoration: "underline", textTransform: "none", letterSpacing: 0 }}>
                  não é esta
                </button>
              )}
            </div>

            {temSugestao && !aTrocarFatura ? (
              <>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginTop: 6 }}>{m.nome_emitente || "—"}</div>
                <div style={{ fontSize: 12, color: T.textMid, marginTop: 5 }}>
                  {m.numero_documento || "—"} · {m.fatura_data ? fmtDate(m.fatura_data) : "—"}
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: divergeValorSugestao ? T.red : T.text, marginTop: 6, fontVariantNumeric: "tabular-nums" }}>
                  {fmt(valorFatura)}
                </div>
                {divergeValorSugestao && (
                  <div style={{ fontSize: 11.5, color: T.red, marginTop: 3, display: "flex", alignItems: "center", gap: 4 }}>
                    <AlertTriangle size={11} strokeWidth={2.5} />
                    difere {fmt(Math.abs(Math.abs(valorFatura) - valorMov))} do movimento
                  </div>
                )}
              </>
            ) : (
              <div style={{ position: "relative", marginTop: 8 }}>
                <Search size={14} color={T.textLight} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
                <input
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                  placeholder="Procurar por nome, número ou valor…"
                  style={{ width: "100%", padding: "7px 10px 7px 30px", borderRadius: 8, border: `1px solid ${T.border}`, fontSize: 12.5, color: T.text, background: T.bg, outline: "none", boxSizing: "border-box" }}
                />
              </div>
            )}
          </div>

          <div style={{ flex: 1, minHeight: 0, overflowY: mostrarPicker ? "auto" : "hidden", background: mostrarPicker ? T.surface : T.bg }}>
            {mostrarPicker ? (
              <div style={{ padding: "10px 14px", display: "flex", flexDirection: "column", gap: 6 }}>
                {/* Fatura que ainda não existe no sistema: extrai do PDF,
                    confirma, cria+arquiva, e concilia pelo caminho normal
                    (onAssociar) -- ver DropFaturaNova. */}
                <DropFaturaNova
                  movimento={m}
                  empresaNif={empresa}
                  desativado={busy}
                  onFaturaCriada={(faturaId) => onAssociar(m, [faturaId])}
                />
                <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "4px 0 2px" }}>
                  <span style={{ flex: 1, height: 1, background: T.border }} />
                  <span style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: ".05em" }}>ou escolhe uma já em sistema</span>
                  <span style={{ flex: 1, height: 1, background: T.border }} />
                </div>
                {carregandoFaturas ? (
                  <div style={{ padding: "24px 0", textAlign: "center", color: T.textLight, fontSize: 12.5 }}>A carregar faturas…</div>
                ) : candidatos.length === 0 ? (
                  <div style={{ padding: "24px 8px", textAlign: "center", color: T.textLight, fontSize: 12.5, lineHeight: 1.5 }}>
                    {filtro
                      ? "Nenhuma fatura corresponde a esta pesquisa."
                      : listaCompleta.length > 0
                      ? `Nenhuma fatura com data/valor próximos (de ${listaCompleta.length} pendente(s)). Pesquisa para ver todas.`
                      : "Sem faturas pendentes desta empresa."}
                  </div>
                ) : (
                  candidatos.map((c, i) => {
                    const sel = selecionadas.has(c.id);
                    const melhor = i === 0 && !filtro && scoreProximidade(c, m) >= 60;
                    return (
                      <label key={c.id} style={{
                        display: "flex", alignItems: "center", gap: 9, padding: "8px 11px", borderRadius: 8,
                        border: `1px solid ${sel ? T.blueBorder : melhor ? T.greenBorder : T.border}`,
                        background: sel ? T.blueBg : T.surface, cursor: "pointer",
                      }}>
                        <input type="checkbox" checked={sel} style={{ flexShrink: 0 }} onChange={() => setSelecionadas((prev) => {
                          const next = new Set(prev);
                          if (next.has(c.id)) next.delete(c.id); else next.add(c.id);
                          return next;
                        })} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            <span style={{ fontSize: 12.5, fontWeight: 700, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.nome_emitente || "—"}</span>
                            {melhor && (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 9, fontWeight: 700, color: T.green, background: T.greenBg, border: `1px solid ${T.greenBorder}`, borderRadius: 20, padding: "1px 5px", flexShrink: 0 }}>
                                <Target size={8} strokeWidth={3} /> mais provável
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 11, color: T.textMid, marginTop: 1 }}>{c.numero_documento} · {fmtDate(c.data_documento)}</div>
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <span style={{ fontSize: 12.5, fontWeight: 700, color: T.text, fontVariantNumeric: "tabular-nums" }}>{fmt(c.valor_em_divida ?? c.valor_total)}</span>
                          {(c.valor_pago || 0) > 0 && <div style={{ fontSize: 9.5, color: T.amber, marginTop: 1 }}>{c.percentagem_paga}% pago · total {fmt(c.valor_total)}</div>}
                        </div>
                        {c.drive_link && (
                          <button
                            type="button"
                            title="Ver PDF desta fatura"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); openPdf(c.drive_link, c.numero_documento); }}
                            style={{ flexShrink: 0, width: 26, height: 26, borderRadius: 6, border: `1px solid ${T.border}`, background: T.surface, color: T.textMid, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                          >
                            <Eye size={13} strokeWidth={2.5} />
                          </button>
                        )}
                      </label>
                    );
                  })
                )}
              </div>
            ) : m.drive_link ? (
              <DrivePreview driveLink={m.drive_link} empresa={empresa} titulo="Fatura" />
            ) : (
              <div style={{ padding: "28px 20px", textAlign: "center", color: T.textLight, fontSize: 12.5 }}>
                Esta fatura não tem PDF arquivado.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Rodapé: soma / erro / ações */}
      {selecionadas.size > 0 && (
        <div style={{ padding: "8px 20px", fontSize: 12, background: bloqueadoPorSoma ? T.redBg : pagamentoParcial ? T.amberBg : T.greenBg, color: bloqueadoPorSoma ? T.red : pagamentoParcial ? T.amber : T.green, borderTop: `1px solid ${bloqueadoPorSoma ? T.redBorder : pagamentoParcial ? T.amberBorder : T.greenBorder}`, flexShrink: 0 }}>
          {selecionadas.size} fatura(s) · saldo selecionado {fmt(somaSelecionadas)}
          {bloqueadoPorSoma
            ? ` — o movimento excede ou não fecha por ${fmt(Math.abs(somaSelecionadas - valorMov))}`
            : pagamentoParcial
            ? ` — pagamento parcial de ${fmt(valorMov)}; ficam ${fmt(somaSelecionadas - valorMov)} em dívida`
            : selecionadas.size > 1 && " — soma exata, vai criar um batch de pagamento já confirmado como pago"}
        </div>
      )}
      {bloqueadoPorSoma && (
        <div style={{ padding: "6px 20px", fontSize: 11.5, color: T.textMid, flexShrink: 0 }}>
          O movimento não pode exceder o saldo em dívida. Para várias faturas, a soma dos saldos tem de corresponder exatamente ao movimento.
        </div>
      )}
      {erro && (
        <div style={{ padding: "9px 20px", fontSize: 12.5, background: T.redBg, color: T.red, borderTop: `1px solid ${T.redBorder}`, flexShrink: 0 }}>
          ✗ Não foi possível concluir: {erro}
        </div>
      )}

      <div style={{ padding: "12px 20px", borderTop: `1px solid ${T.border}`, background: T.bg, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexShrink: 0 }}>
        <button
          disabled={busy || jaResolvido}
          onClick={() => onResolverSemFatura(m)}
          title="Este movimento nunca vai ter fatura (juros, comissões bancárias, transferências internas)"
          style={{ ...botaoSecundario, opacity: busy || jaResolvido ? 0.5 : 1, display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <Ban size={13} strokeWidth={2.5} /> Nunca terá fatura
        </button>

        <div style={{ display: "flex", gap: 8 }}>
          {temSugestao && !aTrocarFatura ? (
            <>
              <button disabled={busy} onClick={() => onRejeitarSugestao(m.match_id!)} style={{ ...botaoSecundario, opacity: busy ? 0.5 : 1 }}>
                Rejeitar
              </button>
              <button disabled={busy} onClick={() => onConfirmarSugestao(m.match_id!)}
                style={{ ...botaoPrimario(T.green), opacity: busy ? 0.5 : 1 }}>
                <Check size={14} strokeWidth={2.5} /> Confirmar
              </button>
            </>
          ) : (
            <button
              disabled={busy || selecionadas.size === 0 || bloqueadoPorSoma}
              onClick={() => onAssociar(m, Array.from(selecionadas))}
              style={{ ...botaoPrimario(T.green), opacity: busy || selecionadas.size === 0 || bloqueadoPorSoma ? 0.5 : 1 }}
            >
              <Check size={14} strokeWidth={2.5} />
              {selecionadas.size > 1 ? `Criar batch e associar (${selecionadas.size})` : pagamentoParcial ? "Associar pagamento parcial" : "Associar e dar como paga"}
            </button>
          )}
        </div>
      </div>
    </Overlay>
  );
}

// ── Auxiliares de apresentação ──────────────────────────────────────────
// Quase ecrã inteiro de propósito: o painel mostra DOIS documentos lado a
// lado (extrato + fatura) -- com um modal pequeno os PDFs ficavam
// ilegíveis, que era metade do problema que este redesenho veio resolver.
// Sem limite máximo em px: num monitor grande o espaço extra é todo
// aproveitado pelos documentos.
function Overlay({ children, onClose }: { children: React.ReactNode; onClose?: () => void }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 3600, background: "rgba(15,23,42,.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: 12 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: T.surface, borderRadius: 12, width: "98vw", height: "96vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 24px 60px rgba(0,0,0,.3)" }}>
        {children}
      </div>
    </div>
  );
}

const rotulo: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase",
};

const botaoSecundario: React.CSSProperties = {
  padding: "8px 14px", borderRadius: 8, border: `1px solid ${T.border}`,
  background: T.surface, color: T.textMid, fontSize: 12.5, fontWeight: 600, cursor: "pointer",
};

const botaoPrimario = (cor: string): React.CSSProperties => ({
  display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 18px", borderRadius: 8,
  border: "none", background: cor, color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer",
});

const botaoIcone = (desativado: boolean): React.CSSProperties => ({
  width: 30, height: 30, borderRadius: 7, border: `1px solid ${T.border}`, background: T.surface,
  color: T.textMid, display: "inline-flex", alignItems: "center", justifyContent: "center",
  cursor: desativado ? "default" : "pointer", opacity: desativado ? 0.4 : 1,
});
